import React from 'react';

import { ApolloProvider as ApolloHooksProvider } from '@apollo/react-hooks';
import { datadogLogs } from '@datadog/browser-logs';
import { InMemoryCache } from 'apollo-cache-inmemory';
import ApolloClient from 'apollo-client';
import { ApolloLink, Operation } from 'apollo-link';
import { onError } from 'apollo-link-error';
import { createHttpLink } from 'apollo-link-http';
import MuiThemeProvider from 'material-ui/styles/MuiThemeProvider';
import { ApolloProvider } from 'react-apollo';
import ReactDOM from 'react-dom';
import ReactGA from 'react-ga4';
import { Provider } from 'react-redux';
import { HashRouter as Router } from 'react-router-dom';

import { IntegrationsConfigsUniverseProvider } from 'Components-redesign/IntegrationsConfigsUniverse';

import './index.css';
import App from './Components/App';
import store from './store';

const REACT_APP_PROJECT_ID = process.env.REACT_APP_PROJECT_ID;

declare global {
  interface Window {
    __DEV__?: boolean;
  }
}

// __DEV__ is true when we are in local development mode
window.__DEV__ = process.env.NODE_ENV !== 'production';

const url =
  REACT_APP_PROJECT_ID || `https://djpqty2rsb.execute-api.eu-north-1.amazonaws.com/prod/graphql`;

if (process.env.REACT_APP_GA_ID) {
  ReactGA.initialize(process.env.REACT_APP_GA_ID);
}

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) => {
      console.warn(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`);
    });
  }
  if (networkError) {
    console.warn(`[Network error]: ${networkError}`);
  }
});

const AuthLink = (operation: Operation, forward: any) => {
  const token = store.getState().authToken;
  const subToken = store.getState().subuserAuthToken;

  // The admin account token should be updated on "RefreshToken" mutation, even logged into a subuser account
  const refreshToken: string | null = operation.operationName == 'RefreshToken' ? token : null;

  operation.setContext((context: any) => ({
    ...context,
    headers: {
      ...context.headers,
      authorization: `Bearer ${refreshToken || subToken || token}`,
    },
  }));

  return forward(operation);
};

/**
 * Adds the name of the query/mutation to the URL for easier
 * debugging in the network log. Only active in local dev
 */
const addOpNameToUrl = (uri: string, options: any) => {
  const { operationName } = JSON.parse(options.body);
  return fetch(`${uri}graphql?q=${operationName}`, options);
};

const link = ApolloLink.from([
  errorLink,
  AuthLink as any,
  createHttpLink({
    uri: url,
    fetch: window.__DEV__ ? addOpNameToUrl : undefined,
  }),
]);

const client = new ApolloClient({
  link: errorLink.concat(link),
  cache: new InMemoryCache({
    dataIdFromObject: (object) => object.id,
  }),
  name: 'Venue Manager',
});

/**
 * Send console logs to datadog (monitoring).
 * We init even when __DEV__ is true but only send to API when not
 */

const API_URL: string = process.env.REACT_APP_PROJECT_ID!;
const [, env] = API_URL.split('.');

datadogLogs.init({
  // API token
  clientToken: process.env.REACT_APP_DATADOG_CLIENT_TOKEN!,

  // Automatically log all errors
  forwardErrorsToLogs: true,

  // Reduce this if the log volume is too high
  sampleRate: 100,

  // Tag the logs with the current environment
  env: env === 'baemingo' ? 'production' : env,
});

datadogLogs.logger.setHandler(window.__DEV__ ? 'console' : ['http', 'console']);
datadogLogs.logger.setLevel(window.__DEV__ ? 'debug' : 'info');
datadogLogs.addLoggerGlobalContext('project', 'back-office');

ReactDOM.render(
  <Provider store={store}>
    <ApolloProvider client={client}>
      <ApolloHooksProvider client={client}>
        <IntegrationsConfigsUniverseProvider>
          <MuiThemeProvider>
            <Router>
              <App />
            </Router>
          </MuiThemeProvider>
        </IntegrationsConfigsUniverseProvider>
      </ApolloHooksProvider>
    </ApolloProvider>
  </Provider>,
  document.getElementById('root'),
);
